import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SURETY_PATH_PREFIX } from "@/platform/productLine";
import { NEW_SUBMISSION_LINES } from "@/lib/submissions/submissionPaths";

// The brand suffix appended to every page-specific title.
const BRAND = "Broker Portal | Signal Specialty";

// The default title (splash/index) — no page prefix, just the brand.
const DEFAULT_TITLE = BRAND;

/**
 * Route → page label mapping.
 *
 * Rules:
 *   - Exact paths take priority; prefix patterns handle parameterised routes.
 *   - Account detail uses a generic label — PII (account names, etc.) must
 *     never appear in a title (screen readers announce it; it lands in history).
 *   - Unknown paths fall back to the 404 label.
 *   - The splash (index "/") uses the bare brand — no prefix.
 */
const ROUTE_TITLES: Array<{ match: (path: string) => boolean; label: string }> =
  [
    // Index / splash — default brand only (no prefix).
    { match: (p) => p === "/", label: "" },
    // Auth flows.
    { match: (p) => p === "/login", label: "Sign in" },
    {
      match: (p) => p === "/forgot-password" || p === "/reset-password",
      label: "Reset your password",
    },
    // Legal (BRK-S149) — public, like the auth flows.
    { match: (p) => p === "/terms", label: "Terms of Service" },
    {
      match: (p) => p === "/connect/bank/closed",
      label: "Bank connection not finished",
    },
    // Protected area.
    { match: (p) => p === "/portal", label: "Portal" },
    { match: (p) => p === "/direct-markets", label: "Direct Markets" },
    // Surety platform — routed off the one prefix that defines it, rather than
    // re-typing "/surety" per entry (src/platform/productLine.ts owns it).
    // Surety submissions — /new must match before the :id pattern, and the
    // resumed order uses a generic label (an insured's name is PII).
    {
      match: (p) => p === `${SURETY_PATH_PREFIX}/submissions/new`,
      label: "New bond submission",
    },
    // The bid result screen and the won step (BRK-S183) — before the generic
    // `/:id` label below, which would otherwise claim them.
    {
      match: (p) =>
        p.startsWith(`${SURETY_PATH_PREFIX}/submissions/`) &&
        p.endsWith("/bid-result"),
      label: "Bid result",
    },
    {
      match: (p) =>
        p.startsWith(`${SURETY_PATH_PREFIX}/submissions/`) &&
        p.endsWith("/won"),
      label: "Won bid",
    },
    {
      // Both `/:id` (the profile) and `/:id/continue` (the flow) — one generic
      // label, because an insured's name is PII and must never reach a title.
      match: (p) => p.startsWith(`${SURETY_PATH_PREFIX}/submissions/`),
      label: "Bond submission",
    },
    {
      match: (p) => p === `${SURETY_PATH_PREFIX}/submissions`,
      label: "Bond submissions",
    },
    // The brokerage's ISSUED bonds (BRK-S181) — the nav's Bonds.
    { match: (p) => p === `${SURETY_PATH_PREFIX}/bonds`, label: "Bonds" },
    // Surety accounts — the list, then the profile. The profile's label is
    // GENERIC: an insured's name is PII and must never reach a title.
    {
      match: (p) => p === `${SURETY_PATH_PREFIX}/accounts`,
      label: "Accounts",
    },
    // Request more capacity (BRK-S329) — before the generic account label.
    {
      match: (p) =>
        p.startsWith(`${SURETY_PATH_PREFIX}/accounts/`) &&
        p.endsWith("/capacity-request"),
      label: "Request more capacity",
    },
    {
      match: (p) => p.startsWith(`${SURETY_PATH_PREFIX}/accounts/`),
      label: "Account",
    },
    // One invoice (BRK-S187). Generic: the number is not PII, but the title
    // table names routes, not records.
    {
      match: (p) => p.startsWith(`${SURETY_PATH_PREFIX}/invoices/`),
      label: "Invoice",
    },
    {
      match: (p) => p === `${SURETY_PATH_PREFIX}/construction`,
      label: "Construction Bond",
    },
    {
      match: (p) => p === `${SURETY_PATH_PREFIX}/commercial`,
      label: "Commercial Bonds",
    },
    // The notification surfaces (BRK-S157 / BRK-S158) — missing from this
    // table until BRK-S181, so both titled "Page not found". The sweep in
    // test/lib/usePageTitle.test.ts now fails for any route declared in
    // src/routes.tsx that this table does not title.
    { match: (p) => p === "/notifications", label: "Notifications" },
    { match: (p) => p === "/preferences", label: "Notification preferences" },
    { match: (p) => p === "/profile", label: "Profile" },
    { match: (p) => p === "/security", label: "Account security" },
    // Accounts — order matters: /accounts/new must match before the :id pattern.
    { match: (p) => p === "/accounts/new", label: "Add account" },
    {
      // /accounts/:id — any path segment after /accounts/ that isn't "new".
      // Using a generic label; account names are PII and must not appear in titles.
      match: (p) => /^\/accounts\/.+$/.test(p) && p !== "/accounts/new",
      label: "Account",
    },
    { match: (p) => p === "/accounts", label: "Accounts" },
    // Submissions — /submissions/new and each line's own URL (BRK-S311) must
    // match before the :id pattern.
    { match: (p) => p === "/submissions/new", label: "New submission" },
    ...NEW_SUBMISSION_LINES.map((l) => ({
      match: (p: string) => p === `/submissions/${l.slug}/new`,
      label: l.title,
    })),
    {
      // A saved Workers' Comp application at any of its steps (BRK-S301).
      match: (p) =>
        /^\/submissions\/wc\/[^/]+(\/[^/]+)?$/.test(p) &&
        p !== "/submissions/wc/new",
      label: "Workers' Comp submission",
    },
    {
      // /submissions/:id — any segment after /submissions/ that isn't "new".
      match: (p) => /^\/submissions\/.+$/.test(p) && p !== "/submissions/new",
      label: "Submission",
    },
    { match: (p) => p === "/submissions", label: "Submissions" },
    { match: (p) => p === "/quotes", label: "Quotes" },
    { match: (p) => p === "/binders", label: "Binders" },
    { match: (p) => p === "/policies", label: "Policies" },
    { match: (p) => p === "/declinations", label: "Declinations" },
  ];

/** The label the table gives a route, or null for one it doesn't know (the
 *  404) and for the splash, which has none. The top bar's breadcrumb reads it
 *  too (src/lib/pageTrail.ts), so a page is named the same in both places. */
export function routeLabel(pathname: string): string | null {
  return ROUTE_TITLES.find((r) => r.match(pathname))?.label || null;
}

/** Resolve the human-readable page label for the current pathname. */
function labelForPath(pathname: string): string {
  const entry = ROUTE_TITLES.find((r) => r.match(pathname));
  // No match → unknown route (404 case).
  return entry === undefined ? "Page not found" : entry.label;
}

/**
 * Sets `document.title` on every route change.
 *
 * Format: `<Page> — Broker Portal | Signal Specialty`
 * Splash: `Broker Portal | Signal Specialty` (no prefix)
 * 404:    `Page not found — Broker Portal | Signal Specialty`
 *
 * Owned here — one place for the entire mapping — so no page component ever
 * touches `document.title` directly (DRY, and eliminates per-page copy-paste).
 *
 * Mount this once inside AppShell so it fires on every client-side navigation.
 */
export function usePageTitle(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const label = labelForPath(pathname);
    document.title = label ? `${label} — ${BRAND}` : DEFAULT_TITLE;
  }, [pathname]);
}
